import {
  CALCULATION_MODE_LABELS,
  formatPercentPoints,
  type BlendCalculationOutcome,
  type BlendResult,
} from '@shared';

/**
 * Renders a blend calculation outcome with a circular target ring and ratio cards.
 * Calculation logic stays in the shared engine — this is presentation only.
 */
export interface BlendResultViewProps {
  outcome: BlendCalculationOutcome;
  onNewCalculation?: () => void;
  onSaveAsRitual?: () => void;
}

function fmtMg(v: number): string {
  return `${v.toFixed(2)} mg`;
}

function fmtGrams(v: number, decimals: number): string {
  return `${v.toFixed(decimals)} g`;
}

export function BlendResultView({
  outcome,
  onNewCalculation,
  onSaveAsRitual,
}: BlendResultViewProps): JSX.Element {
  if (outcome.weightProblem) {
    const wp = outcome.weightProblem;
    return (
      <div className="card stack">
        <h2 className="mt-0">Weight not measurable</h2>
        <div className="notice" role="alert">
          {wp.message}
        </div>
        <p>Try one of the nearest measurable total weights:</p>
        <div className="row">
          {wp.nearestLowerGrams !== null && (
            <span className="badge">{wp.nearestLowerGrams} g</span>
          )}
          {wp.nearestHigherGrams !== null && (
            <span className="badge">{wp.nearestHigherGrams} g</span>
          )}
        </div>
        <p className="hint">
          Your requested weight was not changed. Adjust it and calculate again.
        </p>
      </div>
    );
  }

  const result = outcome.result;
  if (!result) {
    return (
      <div className="card">
        <p className="muted">No measurable combination was found.</p>
      </div>
    );
  }

  const decimals = result.displayDecimals;
  const achieved = result.exactlyAchievable || Math.abs(result.differenceMg) < 0.5;
  const ringPct = Math.min(
    100,
    Math.max(
      0,
      result.targetThcMg > 0
        ? (result.estimatedThcMg / result.targetThcMg) * 100
        : 0,
    ),
  );

  const diffWord =
    result.differenceMg > 0
      ? 'above target'
      : result.differenceMg < 0
        ? 'below target'
        : 'on target';

  return (
    <div className="card stack blend-results">
      <div className="spread">
        <div>
          <h2 className="mt-0 page-title" style={{ fontSize: '1.85rem' }}>
            Blend Results
          </h2>
          <p className="page-lead">
            {achieved ? "You're all set!" : 'Closest measurable mix'}
          </p>
        </div>
        <span className="badge">{CALCULATION_MODE_LABELS[result.mode]}</span>
      </div>

      {!outcome.satisfiedMode && (
        <div className="notice" role="alert">
          The selected mode could not be satisfied with these strains and
          weight. Showing the closest possible measurable result.
        </div>
      )}

      {outcome.targetOutOfRange && (
        <div className="notice" role="status">
          That target isn’t reachable with these two strains and this joint
          size (range{' '}
          {result.reachability.minThcMg.toFixed(1)}–
          {result.reachability.maxThcMg.toFixed(1)} mg). Here’s the closest
          mix we can measure.
        </div>
      )}

      <div className="blend-ring" role="img" aria-label={`Estimated ${fmtMg(result.estimatedThcMg)}; target ${fmtMg(result.targetThcMg)}`}>
        <svg viewBox="0 0 120 120" className="blend-ring__svg" aria-hidden="true">
          <circle
            className="blend-ring__track"
            cx="60"
            cy="60"
            r="52"
            fill="none"
            strokeWidth="10"
          />
          <circle
            className="blend-ring__progress"
            cx="60"
            cy="60"
            r="52"
            fill="none"
            strokeWidth="10"
            strokeDasharray={`${(ringPct / 100) * 326.73} 326.73`}
            transform="rotate(-90 60 60)"
          />
        </svg>
        <div className="blend-ring__label">
          <span className="blend-ring__value">
            {result.estimatedThcMg.toFixed(0)}
          </span>
          <span className="blend-ring__unit">mg THC</span>
          <span className="blend-ring__sub">
            target {result.targetThcMg.toFixed(0)} mg
          </span>
        </div>
      </div>

      <h3 className="calc-step__title" style={{ margin: 0 }}>
        The mix
      </h3>
      <div className="blend-ratio-grid">
        {result.allocations.map((a) => {
          const pct =
            result.totalWeightGrams > 0
              ? (a.grams / result.totalWeightGrams) * 100
              : 0;
          return (
            <div key={a.strainIndex} className="blend-ratio-card">
              <div className="blend-ratio-card__pct">{pct.toFixed(0)}%</div>
              <div className="blend-ratio-card__name">{a.name}</div>
              <div className="muted" style={{ fontSize: '0.85rem' }}>
                {formatPercentPoints(a.thcPercent)} THC
              </div>
              <div className="blend-ratio-card__grams">
                {fmtGrams(a.grams, decimals)}
              </div>
            </div>
          );
        })}
      </div>

      <div className="blend-totals">
        <div>
          <div className="muted">Total weight</div>
          <strong>{fmtGrams(result.totalWeightGrams, decimals)}</strong>
        </div>
        <div>
          <div className="muted">Total THC</div>
          <strong>{fmtMg(result.estimatedThcMg)}</strong>
        </div>
        <div>
          <div className="muted">vs target</div>
          <strong>
            {result.differenceMg >= 0 ? '+' : ''}
            {result.differenceMg.toFixed(1)} mg {diffWord}
          </strong>
        </div>
      </div>

      <details>
        <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
          Formula breakdown
        </summary>
        <pre className="formula">{formulaText(result)}</pre>
      </details>

      {(outcome.closestBelow || outcome.closestAbove) && (
        <div className="stack" style={{ gap: '0.5rem' }}>
          <strong>Closest alternatives</strong>
          {outcome.closestBelow && (
            <div className="muted">
              Closest at/below: {fmtMg(outcome.closestBelow.estimatedThcMg)} (
              {alloc(outcome.closestBelow)})
            </div>
          )}
          {outcome.closestAbove && (
            <div className="muted">
              Closest at/above: {fmtMg(outcome.closestAbove.estimatedThcMg)} (
              {alloc(outcome.closestAbove)})
            </div>
          )}
        </div>
      )}

      <div className="stack" style={{ gap: '0.5rem' }}>
        <div className="grid grid-2" style={{ gap: '0.75rem' }}>
          <button
            type="button"
            className="btn btn-secondary btn-block"
            onClick={onNewCalculation}
            disabled={!onNewCalculation}
          >
            New Calculation
          </button>
          {onSaveAsRitual ? <button type="button" className="btn btn-primary btn-block" onClick={onSaveAsRitual}>Save Ritual</button> : null}
        </div>
      </div>
    </div>
  );
}

function alloc(r: BlendResult): string {
  return r.allocations
    .map((a) => `${a.grams.toFixed(r.displayDecimals)} g`)
    .join(' + ');
}

function formulaText(r: BlendResult): string {
  const lines = r.allocations.map(
    (a) =>
      `${a.name}: ${a.grams.toFixed(r.displayDecimals)} g × ${formatPercentPoints(a.thcPercent)} × 10 = ${a.thcMg.toFixed(2)} mg`,
  );
  lines.push('—'.repeat(24));
  lines.push(`Total: ${r.estimatedThcMg.toFixed(2)} mg theoretical THC`);
  lines.push(`Target: ${r.targetThcMg.toFixed(2)} mg`);
  lines.push(
    `Difference: ${r.differenceMg >= 0 ? '+' : ''}${r.differenceMg.toFixed(2)} mg`,
  );
  return lines.join('\n');
}
